import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Trophy, MapPin, Calendar, User, CheckCircle2, Loader, Ban, ArrowLeft, Lock } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import ImageUpload, { fileUrl } from "@/components/ImageUpload";
import CustomFieldInput from "@/components/tournament/CustomFieldInput";
import AdShowcase from "@/components/AdShowcase";
import ProofReviewDialog from "@/components/tournament/ProofReviewDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { categoryLabel, fmtMoney, fmtDate, PAYMENT_STATUS_META } from "@/lib/tournament";
import api, { formatApiError } from "@/lib/api";

const inputCls = "rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";

export default function RegisterTournament() {
  const { tid } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState(null); // null loading | false error | data
  const [selectedCatId, setSelectedCatId] = useState("");
  const [partnerEmail, setPartnerEmail] = useState("");
  const [responses, setResponses] = useState({});
  const [busyCat, setBusyCat] = useState(null);
  const [waitlistAsk, setWaitlistAsk] = useState(null);
  const [verify, setVerify] = useState(null); // { cat, email }
  const [pendingBody, setPendingBody] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [proofBusy, setProofBusy] = useState(null);
  const [flag, setFlag] = useState(null);

  const load = useCallback(() => {
    api.get(`/tournaments/${tid}/public`).then(({ data }) => setState(data)).catch((e) => setState({ error: e.response?.status || 500 }));
  }, [tid]);

  useEffect(() => { load(); }, [load]);

  if (state === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;

  if (state.error) return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Registration" />
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="register-unavailable">
        <Lock className="w-10 h-10 mx-auto text-primary-c opacity-60" />
        <h2 className="font-display font-bold text-xl mt-4">{state.error === 403 ? "Registration isn't open yet" : "Tournament not found"}</h2>
        <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{state.error === 403 ? "The organizer hasn't opened registration for this event." : "This link may be invalid or the event was removed."}</p>
        <button onClick={() => nav("/dashboard")} className="btn-glow rounded-full px-5 py-2.5 mt-6 font-bold">Back to dashboard</button>
      </div>
    </div>
  );

  const t = state.tournament;
  const myByCat = {};
  (state.my_registrations || []).forEach((r) => { myByCat[r.category_id] = r; });

  const spots = state.spots || {};
  const register = async (cat, joinWaitlist = false) => {
    setBusyCat(cat.id);
    try {
      const body = { category_id: cat.id, join_waitlist: joinWaitlist };
      if (cat.discipline === "doubles") {
        const pe = partnerEmail.trim();
        if (!pe) { toast.error("Enter your partner's email"); setBusyCat(null); return; }
        body.partner_email = pe;
      }
      const customFields = t.custom_fields || [];
      const missing = customFields.find((f) => f.required && f.type !== "header" && !String(responses[f.id] || "").trim());
      if (missing) { toast.error(`"${missing.label}" is required`); setBusyCat(null); return; }
      if (customFields.length) body.custom_responses = responses;
      const { data } = await api.post(`/tournaments/${tid}/register/request-code`, body);
      setPendingBody(body);
      setCode("");
      setVerify({ cat, email: data.email });
      toast.success(`We sent a 6-digit code to ${data.email}`);
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail === "CATEGORY_FULL") setWaitlistAsk(cat);
      else toast.error(formatApiError(e.response?.data?.detail) || "Registration failed");
    } finally { setBusyCat(null); }
  };

  const confirmVerify = async () => {
    if (!verify) return;
    setVerifying(true);
    try {
      const { data } = await api.post(`/tournaments/${tid}/register/verify`, { category_id: verify.cat.id, code: code.trim() });
      toast.success(data.waitlisted ? "You're on the waitlist — we'll email you if you're accepted." : "Registered! Complete payment to confirm.");
      setVerify(null); setPendingBody(null); setPartnerEmail(""); setResponses({});
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
    } finally { setVerifying(false); }
  };

  const resendRegCode = async () => {
    if (!pendingBody) return;
    try { await api.post(`/tournaments/${tid}/register/request-code`, pendingBody); toast.success("A new code is on its way"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not resend"); }
  };

  const uploadProof = async (rid, url) => {
    if (!url) return;
    setProofBusy(rid);
    try {
      const { data } = await api.post(`/registrations/${rid}/proof`, { proof_url: url });
      if (data.proof_status === "verified") toast.success("Payment verified — we emailed you. Your application is waiting for approval.");
      else if (data.proof_status === "flagged") setFlag({ rid, reasons: data.reasons, expected: data.expected_amount, detected: data.amount_detected });
      else toast.success("Receipt uploaded — the organizer will review it manually.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save proof"); }
    finally { setProofBusy(null); }
  };
  const amountDue = (c) => Number(t.entry_fee || 0) * (c.discipline === "doubles" ? 2 : 1);

  const withdraw = async (rid) => {
    try { await api.delete(`/registrations/${rid}`); toast.success("Registration withdrawn"); load(); }
    catch (e) { toast.error("Could not withdraw"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Registration" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grain">
        <button onClick={() => nav("/my-tournaments")} data-testid="register-my-link" className="text-sm flex items-center gap-2 mb-5" style={{ color: "var(--text-2)" }}><ArrowLeft className="w-4 h-4" /> My tournaments</button>

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="rounded-3xl glow-border overflow-hidden" style={{ background: "var(--surface)" }} data-testid="register-hero">
          <div className="relative aspect-[4/1] min-h-[120px]" style={{ background: "var(--bg)" }}>
            {t.banner_url ? <img src={fileUrl(t.banner_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Trophy className="w-10 h-10 text-primary-c opacity-30" /></div>}
          </div>
          <div className="p-6">
            <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl break-words" data-testid="register-name">{t.name}</h1>
            <div className="flex flex-wrap gap-4 mt-3 text-sm" style={{ color: "var(--text-2)" }}>
              {t.venue && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary-c" />{t.venue}</span>}
              {(t.start_date || t.end_date) && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-primary-c" />{[fmtDate(t.start_date), fmtDate(t.end_date)].filter(Boolean).join(" → ")}</span>}
              {t.organizer_name && <span className="flex items-center gap-1.5"><User className="w-4 h-4 text-primary-c" />{t.organizer_name}</span>}
            </div>
          </div>
        </motion.div>

        {/* payment info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
          <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="register-fee">
            <div className="text-xs uppercase font-bold tracking-wider" style={{ color: "var(--text-2)" }}>Entry fee</div>
            <div className="font-display font-black text-3xl tracking-tighter mt-1">{fmtMoney(t.currency, t.entry_fee)}</div>
            {t.payment_details && <p className="text-sm mt-3 whitespace-pre-wrap rounded-xl p-3" style={{ background: "var(--bg)", color: "var(--text-2)" }} data-testid="register-pay-details">{t.payment_details}</p>}
          </div>
          {t.payment_qr_url && (
            <div className="rounded-2xl glow-border p-5 flex flex-col items-center justify-center" style={{ background: "var(--surface)" }} data-testid="register-qr">
              <div className="text-xs uppercase font-bold tracking-wider mb-3" style={{ color: "var(--text-2)" }}>Scan to pay</div>
              <img src={fileUrl(t.payment_qr_url)} alt="payment qr" className="w-44 h-44 object-contain rounded-xl p-2" style={{ background: "#fff" }} />
            </div>
          )}
        </div>

        {/* register for a category — dropdown */}
        <h3 className="font-display font-bold text-lg mt-8 mb-3">Register for a category</h3>
        {(() => {
          const openCats = (t.categories || []).filter((c) => !myByCat[c.id] || myByCat[c.id].payment_status === "rejected");
          const selCat = openCats.find((c) => c.id === selectedCatId);
          if ((t.categories || []).length === 0) return <p className="text-sm" style={{ color: "var(--text-2)" }}>No categories are open for this event.</p>;
          if (openCats.length === 0) return <p className="text-sm rounded-2xl glow-border p-5" style={{ background: "var(--surface)", color: "var(--text-2)" }} data-testid="register-all-done">You're registered in every category for this event. 🎉</p>;
          return (
            <div className="rounded-2xl glow-border p-5 space-y-4" style={{ background: "var(--surface)" }} data-testid="register-picker">
              <div>
                <label className="text-xs uppercase font-bold tracking-wider" style={{ color: "var(--text-2)" }}>Category</label>
                <Select value={selectedCatId} onValueChange={(v) => { setSelectedCatId(v); setPartnerEmail(""); }}>
                  <SelectTrigger data-testid="register-cat-select" className="mt-1.5 rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue placeholder="Select a category to join" /></SelectTrigger>
                  <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                    {openCats.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`register-opt-${c.id}`}>{categoryLabel(c)} · {c.discipline}{spots[c.id]?.max ? ` · ${spots[c.id].full ? "FULL — waitlist" : `${spots[c.id].max - spots[c.id].taken} spots left`}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {selCat && spots[selCat.id]?.max && (
                <div data-testid="register-spots" className="text-xs rounded-xl px-3 py-2" style={spots[selCat.id].full ? { background: "rgba(245,158,11,0.14)", color: "#b45309" } : { background: "var(--bg)", color: "var(--text-2)" }}>
                  {spots[selCat.id].full
                    ? `This category is full (${spots[selCat.id].taken}/${spots[selCat.id].max}). You can still join the waitlist${spots[selCat.id].waitlist ? ` — ${spots[selCat.id].waitlist} already waiting` : ""}.`
                    : `${spots[selCat.id].taken}/${spots[selCat.id].max} entries · ${spots[selCat.id].max - spots[selCat.id].taken} spots left`}
                </div>
              )}

              {selCat && selCat.discipline === "doubles" && (
                <div data-testid="register-partner-block">
                  <label className="text-xs uppercase font-bold tracking-wider" style={{ color: "var(--text-2)" }}>Partner's email</label>
                  <input data-testid="register-partner-email" value={partnerEmail} onChange={(e) => setPartnerEmail(e.target.value)} placeholder="Your partner's account email" className={`${inputCls} w-full mt-1.5`} style={{ color: "var(--text)" }} />
                  <p className="text-[11px] mt-1.5" style={{ color: "var(--text-2)" }}>Your partner needs a PointControl account and will get an invite to accept.</p>
                </div>
              )}

              {(t.custom_fields || []).length > 0 && (
                <div className="space-y-4 pt-1" data-testid="register-custom-fields">
                  {(t.custom_fields || []).map((f) => (
                    <CustomFieldInput key={f.id} field={f}
                      value={responses[f.id]} onChange={(v) => setResponses((r) => ({ ...r, [f.id]: v }))} />
                  ))}
                </div>
              )}

              <button data-testid="register-submit" onClick={() => selCat && register(selCat)} disabled={!selCat || busyCat === selCat?.id}
                className="btn-glow rounded-full px-6 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                {busyCat && selCat && busyCat === selCat.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Register
              </button>
            </div>
          );
        })()}

        {/* my registrations */}
        {(state.my_registrations || []).length > 0 && (
          <>
            <h3 className="font-display font-bold text-lg mt-8 mb-3">Your registrations</h3>
            <div className="space-y-3" data-testid="register-my-list">
              {(t.categories || []).filter((c) => myByCat[c.id]).map((c) => {
                const reg = myByCat[c.id];
                return (
                  <div key={c.id} data-testid={`register-cat-${c.id}`} className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <div className="font-display font-bold">{categoryLabel(c)}</div>
                        <div className="text-[10px] mono uppercase mt-0.5" style={{ color: "var(--text-2)" }}>{c.discipline}</div>
                      </div>
                      <RegStatus reg={reg} />
                    </div>
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                      {reg.payment_status === "rejected" ? (
                        <div className="text-sm rounded-xl px-3 py-2" style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }} data-testid={`rejection-note-${c.id}`}>
                          <b>Not accepted:</b> {reg.rejection_reason || "No reason given"}. You can register again in a category that fits.
                        </div>
                      ) : reg.waitlisted ? (
                        <p className="text-sm" style={{ color: "var(--text-2)" }} data-testid={`waitlist-note-${c.id}`}>You're on the waitlist. We'll email you if a spot opens or the organizer accepts you — no payment needed yet.</p>
                      ) : proofBusy === reg.registration_id ? (
                        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-2)" }} data-testid={`proof-checking-${c.id}`}><Loader2 className="w-4 h-4 animate-spin text-primary-c" /> Checking your receipt…</div>
                      ) : reg.payment_proof_url && reg.proof_status !== "flagged" ? (
                        <div className="flex items-center gap-3">
                          <img src={fileUrl(reg.payment_proof_url)} alt="proof" className="w-16 h-16 object-cover rounded-lg glow-border" data-testid={`my-proof-${c.id}`} />
                          <div className="text-sm" style={{ color: "var(--text-2)" }} data-testid={`my-proof-status-${c.id}`}>
                            {reg.proof_status === "verified" ? <><b style={{ color: "#22c55e" }}>Payment verified.</b> Waiting for the organizer's approval.</>
                              : reg.proof_status === "manual_review" ? <><b style={{ color: "#eab308" }}>Under manual review.</b> Your application stays unpaid until the organizer checks your receipt.
                                  <div className="mt-2 max-w-xs" data-testid={`proof-replace-${c.id}`}><ImageUpload testid={`proof-replace-${c.id}`} label="Upload a different receipt" value={null} onChange={(url) => uploadProof(reg.registration_id, url)} aspect="aspect-[3/1]" privateFile keepOriginal /></div></>
                              : "Payment proof submitted."}
                          </div>
                        </div>
                      ) : (
                        <div className="max-w-xs" data-testid={`proof-upload-${c.id}`}>
                          {reg.proof_status === "flagged" && (
                            <div className="text-xs rounded-xl px-3 py-2 mb-2" style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }} data-testid={`proof-flagged-${c.id}`}>
                              Your last receipt was flagged. Upload a new one or{" "}
                              <button className="underline font-bold" data-testid={`proof-open-review-${c.id}`} onClick={() => setFlag({ rid: reg.registration_id, reasons: reg.proof_check?.reasons || [], expected: reg.proof_check?.expected_amount, detected: reg.proof_check?.amount_detected })}>request a manual review</button>.
                            </div>
                          )}
                          {amountDue(c) > 0 && <div className="text-xs mb-1" style={{ color: "var(--text-2)" }} data-testid={`amount-due-${c.id}`}>Amount due: <b style={{ color: "var(--text)" }}>{fmtMoney(t.currency, amountDue(c))}</b>{c.discipline === "doubles" ? ` (${fmtMoney(t.currency, t.entry_fee)} × 2 players)` : ""}</div>}
                          <ImageUpload testid={`proof-${c.id}`} label="Upload payment screenshot" value={null} onChange={(url) => uploadProof(reg.registration_id, url)} aspect="aspect-[3/2]" hint="Original, unedited screenshot of your payment" privateFile keepOriginal />
                        </div>
                      )}
                      <button data-testid={`withdraw-${c.id}`} onClick={() => withdraw(reg.registration_id)} className="text-xs mt-3 flex items-center gap-1.5" style={{ color: "#ef4444" }}><Ban className="w-3.5 h-3.5" /> Withdraw</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
          </div>
          <AdShowcase />
          <ProofReviewDialog flag={flag} currency={t.currency} onClose={() => setFlag(null)} onDone={() => { setFlag(null); load(); }} />
        </div>
      </div>

      <Dialog open={!!waitlistAsk} onOpenChange={(o) => !o && setWaitlistAsk(null)}>
        <DialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="waitlist-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">This category is full</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              {waitlistAsk && categoryLabel(waitlistAsk)} has reached its limit. Would you like to join the waitlist? We'll email you that you're waitlisted, and again if you're accepted. No payment is needed while you wait.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button data-testid="waitlist-decline" onClick={() => setWaitlistAsk(null)} className="glass rounded-full px-5 py-2.5 text-sm font-bold" style={{ color: "var(--text)" }}>No thanks</button>
            <button data-testid="waitlist-join" onClick={() => { const c = waitlistAsk; setWaitlistAsk(null); register(c, true); }} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold">Join waitlist</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!verify} onOpenChange={(o) => !o && setVerify(null)}>
        <DialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="register-verify-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Verify your email</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              We sent a 6-digit code to <span className="font-bold" style={{ color: "var(--text)" }}>{verify?.email}</span>. Your registration is created only after you confirm.
            </DialogDescription>
          </DialogHeader>
          <input data-testid="register-verify-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric" autoComplete="one-time-code" placeholder="000000" maxLength={6}
            className="w-full rounded-xl bg-surface glow-border px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
          <button data-testid="register-verify-resend" onClick={resendRegCode} className="text-xs text-left hover:text-primary-c transition-colors" style={{ color: "var(--text-2)" }}>Didn't get it? Resend code</button>
          <DialogFooter>
            <button data-testid="register-verify-cancel" onClick={() => setVerify(null)} className="glass rounded-full px-5 py-2.5 text-sm font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="register-verify-confirm" onClick={confirmVerify} disabled={verifying || code.length !== 6} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Confirm registration</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RegStatus({ reg }) {
  if (reg.waitlisted) return <span data-testid={`reg-status-${reg.category_id}`} className="mono text-[11px] uppercase tracking-wider glass rounded-full px-2.5 py-1" style={{ color: "#b45309" }}>Waitlisted</span>;
  const pm = PAYMENT_STATUS_META[reg.payment_status] || PAYMENT_STATUS_META.pending;
  const Icon = reg.payment_status === "paid" ? CheckCircle2 : Loader;
  return (
    <div className="flex flex-col items-end gap-1" data-testid={`reg-status-${reg.category_id}`}>
      <span className="mono text-[11px] uppercase tracking-wider glass rounded-full px-2.5 py-1 flex items-center gap-1.5" style={{ color: pm.color }}><Icon className="w-3.5 h-3.5" /> {pm.l}</span>
      {reg.discipline === "doubles" && reg.partner && (
        <span className="text-[10px] mono" style={{ color: reg.partner.status === "accepted" ? "#22c55e" : reg.partner.status === "declined" ? "#ef4444" : "#eab308" }}>
          {reg.partner.status === "accepted" ? `Partner: ${reg.partner.name || reg.partner.email}` : reg.partner.status === "declined" ? "Partner declined" : "Partner invite pending"}
        </span>
      )}
    </div>
  );
}
